import { Component, computed, effect, inject, OnInit, Signal, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { AbstractControl, FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatNativeDateModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { ENVIRONMENT } from '../../../../environments/environment.interface';
import { CreateClientBody } from '../../../api/app-types/client.app';
import { ContactPoint, ContactPointCategory } from '../../../api/app-types/contact-point.app';
import { County } from '../../../api/app-types/county.app';
import { Gender } from '../../../api/app-types/gender.app';
import { CLIENT_API } from '../../../api/injection-tokens/services.token';
import { provideClientApi } from '../../../api/services/client.api';
import { AddressCompletion } from '../../../core/components/address-completion/address-completion';
import { LvrContactPoint } from '../../../core/components/contact-point/contact-point';
import { LvrDefaultActions } from '../../../core/components/default-actions/default.actions';
import { LvrField } from '../../../core/components/field/field';
import { LvrHintBanner } from '../../../core/components/hint-banner/hint-banner';
import { LvrButton } from '../../../core/components/lvr-button/lvr-button';
import { LvrInputDirective } from '../../../core/directives/lvr-input.directive';
import { RECORD_MANAGER } from '../../../core/services/record.provide';
import { REPRESENTATION_MODE } from '../../../core/services/representation-mode.service';
import { compareById } from '../../../shared/helpers/compare-by-id.func';
import { mapDateOrNull, matDateProviders } from '../../../shared/helpers/date.func';
import { hasError } from '../../../shared/helpers/has-error.func';
import { isContactPointComplete } from '../../../shared/helpers/is-contact-point-complete.func';
import { validateBirthDateAndSetWarning } from '../../../shared/helpers/validate-birthdate.func';
import { NOTIFICATION_SERVICE } from '../../../shared/notification/notification';
import { createErrorNotification, createSuccessNotification } from '../../../shared/notification/notification.factory';
import { LvrPaddingDirective } from '../../../shared/padding/padding.directive';
import { hasPermission } from '../../../shared/permissions/permission.func';
import { LvrAppendAsteriskPipe } from '../../../shared/pipes/append-asterisk.pipe';
import { LvrAutoSaveDirective } from '../../../shared/storage/auto-save.directive';
import { FORM_KEYS } from '../../../shared/storage/form-keys.type';
import { FormPersistenceService } from '../../../shared/storage/form-persistence.service';
import { LvrRestoreActions } from '../../../shared/storage/restore-actions';
import { SessionManagerService } from '../../../shared/storage/session-manager.service';
import { ImmediateErrorStateMatcher } from '../../../shared/validators/immediate-error-state-matcher';
import { LvrSearchableSelect } from '../../../core/components/searchable-select/searchable-select';

@Component({
	selector: 'lvr-create-client',
	imports: [
		MatFormFieldModule,
		MatSelectModule,
		MatTooltipModule,
		TranslocoModule,
		ReactiveFormsModule,
		MatDatepickerModule,
		MatRadioModule,
		MatIconModule,
		MatButtonModule,
		MatInputModule,
		MatNativeDateModule,
		LvrInputDirective,
		LvrAutoSaveDirective,
		AddressCompletion,
		LvrAppendAsteriskPipe,
		LvrPaddingDirective,
		LvrRestoreActions,
		LvrContactPoint,
		LvrField,
		LvrDefaultActions,
		LvrHintBanner,
		LvrButton,
		LvrSearchableSelect,
	],
	templateUrl: './create-client.html',
	styleUrl: './create-client.scss',
	providers: [provideClientApi, matDateProviders],
})
export class CreateClient implements OnInit {
	public readonly compareById = compareById;
	public readonly hasError = hasError;
	public readonly draftId = FORM_KEYS.CREATE_CLIENT;
	public readonly showBirthdayWarning = signal<boolean>(false);
	public readonly showLoading = signal(false);
	public readonly environment = inject(ENVIRONMENT);
	private readonly nameRegex = new RegExp(this.environment.nameRegexStr);
	private readonly titleRegex = new RegExp(this.environment.titleRegexStr);
	public readonly birthDateWarningRange = {
		min: this.environment.formConfiguration.client.birthdateWarningRange.min,
		max: this.environment.formConfiguration.client.birthdateWarningRange.max,
	};
	public readonly dateFormatHint = this.environment.visualDateFormat;
	public readonly birthdateLimitYears = this.environment.birthdateLimitYears; // 100 years
	public maxDate = new Date();
	public minDate = new Date();
	public minDateFormatted = '';
	public readonly form = this.initNewClientForm(
		inject(FormBuilder),
		inject(ActivatedRoute).snapshot?.queryParams ?? {},
	);
	public readonly matcher = new ImmediateErrorStateMatcher();
	public readonly communicationWayCount = signal(1);
	public readonly communicationWayCountArrayKeys = computed(() => Array(this.communicationWayCount()));
	private readonly representationMode = inject(REPRESENTATION_MODE);
	public readonly isRepresentationActive = this.representationMode.isActive;

	private readonly clientApi = inject(CLIENT_API);
	private readonly router = inject(Router);
	private readonly draftService = inject(FormPersistenceService);
	private readonly session = inject(SessionManagerService);
	private readonly recordManager = inject(RECORD_MANAGER);
	private readonly notificationService = inject(NOTIFICATION_SERVICE);
	private readonly transloco = inject(TranslocoService);
	private readonly route = inject(ActivatedRoute);

	private readonly routeData = toSignal(this.route.data);
	public readonly availableGenders: Signal<Gender[]> = computed(() => this.routeData()?.['genders']);
	public readonly availableResidenceDistrict: Signal<County[]> = computed(
		() => this.routeData()?.['counties'].entities || [],
	);
	public readonly contactPointCategories = computed(
		() => (this.routeData()?.['contactPointCategories'].entities || []) as ContactPointCategory[],
	);
	public readonly canCreateClientsPermission = hasPermission(this.session.permissions, [
		'Clients.Create',
		'Clients.Create.PhaseOneOnly',
	]);

	private readonly valueChanged = toSignal(this.form?.valueChanges);
	private readonly dateOfBirthCtrl = this.form.get('dateOfBirth') as AbstractControl<Date | string | undefined>;
	private readonly dateOfBirthValue = toSignal(this.dateOfBirthCtrl.valueChanges, {
		initialValue: this.dateOfBirthCtrl.value,
	});

	public constructor() {
		effect(() => {
			const value = this.dateOfBirthValue();

			if (typeof value !== 'string') return;

			this.dateOfBirthCtrl.setValue(mapDateOrNull(value) ?? undefined, {
				emitEvent: false,
			});
		});

		effect(() => {
			const value = this.valueChanged()?.dateOfBirth;
			validateBirthDateAndSetWarning(value, this.showBirthdayWarning, this.birthDateWarningRange);
		});
	}
	public async ngOnInit() {
		// Set min date for Datepicker
		this.setMinDates();
	}

	/**
	 * Cancel new client creation.
	 */
	public cancel(): void {
		this.draftService.deleteDraft('client', this.draftId);
		this.recordManager.closeAndSwitchToNextRecord();
	}

	/**
	 * Create a new client.
	 */
	public createClient = async () => {
		if (this.showLoading()) return;
		this.showLoading.set(true);
		try {
			const formValue = this.form.value;
			// backend wants complete gender object, not just the ID
			// therefore we get it from the availableGenders
			if (formValue.gender) {
				formValue.gender = this.availableGenders().find((g) => g.id === formValue.gender.id);
			}

			// Handles leading and trailing whitespaces in string form controls
			Object.keys(formValue).forEach((k) => {
				if (typeof formValue[k] === 'string') formValue[k] = formValue[k].trim();
			});

			const body: CreateClientBody = { ...formValue, communicationWays: [] };

			const filteredCws = (formValue.communicationWays ?? []).filter(isContactPointComplete);

			if (filteredCws.length > 0) {
				const additionalCws = filteredCws.map((cp: ContactPoint) => ({
					preferredWayOfContact: cp.preferredWayOfContact,
					categoryContactPointId: cp.categoryContactPointId,
					contactValue: cp.contactValue?.trim(),
					remark: cp.remark?.trim(),
				}));

				body.communicationWays?.push(...additionalCws);
			}

			const createdClient = await this.clientApi.create(body);
			this.notificationService.addNotification(
				createSuccessNotification(this.transloco.translate('base.notification_message.client_created')),
			);
			this.draftService.deleteDraft('client', this.draftId);
			const active = this.recordManager.activeRecord();
			if (active?.id) this.recordManager.closeRecord(active.id);
			setTimeout(() => this.router.navigate(['/desk/client', createdClient.id, 'assignments']), 300);
		} catch (e) {
			console.error(e);
			this.notificationService.addNotification(
				createErrorNotification(this.transloco.translate('base.notification_message.client_create_error')),
			);
		} finally {
			this.showLoading.set(false);
		}
	};

	/**
	 * Sets min dates for the Datepicker.
	 */
	private setMinDates() {
		this.minDate.setFullYear(this.maxDate.getFullYear() - this.birthdateLimitYears);
		this.minDateFormatted = this.minDate.toLocaleDateString('de-DE', {
			year: 'numeric',
			month: '2-digit',
			day: '2-digit',
		});
	}

	/**
	 * Initializes the form for creating a new client.
	 */
	private initNewClientForm(fb: FormBuilder, initialQueryParams: Record<string, string>): FormGroup {
		return fb.group({
			name: fb.control(initialQueryParams['name'] ?? '', [Validators.required, Validators.pattern(this.nameRegex)]),
			gender: fb.control(undefined),
			firstName: fb.control(initialQueryParams['firstName'] ?? '', Validators.pattern(this.nameRegex)),
			birthName: fb.control('', Validators.pattern(this.nameRegex)),
			dateOfBirth: fb.control<Date | undefined>(undefined),
			title: fb.control('', Validators.pattern(this.titleRegex)),

			address: fb.control(''),

			communicationWays: fb.control<ContactPoint[]>([]),
			remark: fb.control(''),

			residenceDistrict: fb.control(undefined),

			healthInsurances: fb.control(''),
			numberKV: fb.control(''),

			numberSV: fb.control(''),
			numberBA: fb.control(''), // Used as 'Kundennummer AfG' in the template
		});
	}

	public addCommunicationWay() {
		this.communicationWayCount.set(this.communicationWayCount() + 1);
	}

	public removeCommunicationWay(index: number) {
		const ctrl = this.form.get('communicationWays');
		if (!ctrl) return;

		const values = (ctrl.value ?? []) as ContactPoint[];
		if (index < 0 || index >= values.length) {
			this.communicationWayCount.set(Math.max(1, this.communicationWayCount() - 1));
			return;
		}

		values.splice(index, 1);
		ctrl.setValue([...values]);

		this.communicationWayCount.set(Math.max(1, this.communicationWayCount() - 1));
	}

	public updateValues(data: { valid: boolean; value: ContactPoint }, index: number) {
		const ctrl = this.form.get('communicationWays');
		if (!ctrl) return;

		const values = (ctrl.value ?? []) as ContactPoint[];

		while (values.length <= index) {
			values.push(this.emptyContactPoint());
		}

		values[index] = { ...values[index], ...data.value };
		ctrl.setValue([...values]);
	}

	private emptyContactPoint(): ContactPoint {
		return {
			id: -1,
			preferredWayOfContact: false,
			categoryContactPointId: -1,
			contactValue: '',
			remark: '',
			personId: -1,
			personTypeId: -1,
		};
	}
}
